import clsx from 'clsx';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { EMOJI_CATEGORIES, recentEmoji, rememberEmoji } from '../lib/emoji';
import { useI18n } from '../lib/i18n';

/** A press that leaves the focus — and the cursor — in the message box. */
const keepFocus = (e: { preventDefault: () => void }) => e.preventDefault();

/**
 * The chat's emoji: a tab a category, the ones used last first. A press puts
 * one where the cursor is; the picker stays open for the next. Its buttons keep
 * the focus off themselves (mouse down does nothing), so the message box keeps
 * its cursor and selection while emoji go in.
 */
export function EmojiPicker({ onPick, className }: { onPick: (emoji: string) => void; className?: string }) {
  const { t } = useI18n();
  const [recent, setRecent] = useState(recentEmoji);
  const [tab, setTab] = useState<string>(() => (recentEmoji().length ? 'recent' : EMOJI_CATEGORIES[0].id));

  const pick = (emoji: string) => {
    rememberEmoji(emoji);
    onPick(emoji);
    // The recent row changes only when it is not the one being picked from, so nothing jumps under the pointer.
    if (tab !== 'recent') setRecent(recentEmoji());
  };

  const tabs = [
    { id: 'recent', label: t('chat.emoji.recent'), icon: null as string | null },
    ...EMOJI_CATEGORIES.map((c) => ({ id: c.id, label: t(c.label), icon: c.icon })),
  ];
  const shown = tab === 'recent' ? recent : (EMOJI_CATEGORIES.find((c) => c.id === tab)?.emoji ?? []);
  const label = tabs.find((x) => x.id === tab)?.label ?? '';

  return (
    <div
      role="dialog"
      aria-label={t('chat.emoji')}
      className={clsx('flex flex-col overflow-hidden rounded-lg surface shadow-xl', className)}
    >
      <div className="flex shrink-0 gap-0.5 overflow-x-auto overflow-y-hidden border-b hairline p-1" role="tablist">
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            aria-label={x.label}
            title={x.label}
            onMouseDown={keepFocus}
            onClick={() => setTab(x.id)}
            className={clsx(
              'grid h-8 w-8 shrink-0 place-items-center rounded-sm text-lg leading-none transition-colors duration-fast',
              tab === x.id
                ? 'bg-primary/12 text-primary-600 dark:text-primary-400'
                : 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
            )}
          >
            {x.icon ?? <Clock className="h-4 w-4" aria-hidden />}
          </button>
        ))}
      </div>
      <p className="shrink-0 px-3 pb-1 pt-2 text-overline uppercase text-[var(--text-tertiary)]">{label}</p>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {shown.length === 0 ? (
          <p className="px-1 py-6 text-center text-body-sm text-muted">{t('chat.emoji.noRecent')}</p>
        ) : (
          <div className="grid grid-cols-8 gap-0.5">
            {shown.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onMouseDown={keepFocus}
                onClick={() => pick(emoji)}
                className="grid h-9 w-full place-items-center rounded-sm text-[22px] leading-none transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
